// @ts-check
import { ThemeEvents } from '@theme/events';
import { showToast } from '@theme/custom.toast';
import { WISHLIST_CHANGE } from '@theme/custom.wishlist';
import { DISCOUNT_OUTCOME } from '@theme/custom.cart-discount-outcome';

/**
 * The one place a theme event becomes a toast.
 *
 * Kept apart from `custom.toast.js` on purpose: that file is the primitive, and any surface may
 * raise a toast through `showToast`. This is the list of surfaces that currently do, so "what pops
 * up, and when" is one file to read rather than a grep across the theme.
 *
 * Copy comes from the JSON `snippets/custom.toast-host.liquid` renders beside the host, so the
 * strings stay with their `t:` keys instead of in a stock global.
 */

/** @typedef {Record<string, string>} ToastCopy */

/**
 * Turns a server error description into text suitable for a toast.
 *
 * @param {unknown} value - The server-provided description.
 * @returns {string}
 */
function readableDescription(value) {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(readableDescription).filter(Boolean).join(' ');
  if (value && typeof value === 'object') {
    return Object.values(value).map(readableDescription).filter(Boolean).join(' ');
  }

  return '';
}

/**
 * The copy block, or an empty object when the host is not on the page.
 *
 * @returns {ToastCopy}
 */
function readCopy() {
  const source = document.querySelector('[data-toast-copy]');
  if (!source?.textContent) return {};

  try {
    return JSON.parse(source.textContent);
  } catch (error) {
    // Every message below would silently become an empty string, so a broken block has to say so.
    console.warn('custom.toast-events: the copy block could not be parsed', error);
    return {};
  }
}

const copy = readCopy();

document.addEventListener(WISHLIST_CHANGE, (event) => {
  if (!(event instanceof CustomEvent)) return;

  showToast({
    tone: 'success',
    title: event.detail.saved ? copy.wishlistAdded : copy.wishlistRemoved,
  });
});

document.addEventListener(ThemeEvents.cartError, (event) => {
  // CartErrorEvent extends Event, not CustomEvent, so `detail` is an own property rather than the
  // typed one - which is also why the cast below is needed.
  if (!('detail' in event)) return;

  // The server's own sentence is the useful one; the generic line is the floor when it sends none.
  const detail =
    /** @type {{ requestId?: string, data?: { message?: string, description?: unknown } }} */ (
      /** @type {any} */ (event).detail
    );

  // `requestId` marks a customizer submission, and the customizer shows the failure in its own
  // footer banner. A toast on top of it reports one failure twice.
  if (detail?.requestId) return;

  showToast({
    tone: 'error',
    title: detail?.data?.message || copy.errorTitle,
    body: readableDescription(detail?.data?.description) || copy.cartError,
  });
});

document.addEventListener(DISCOUNT_OUTCOME, (event) => {
  if (!(event instanceof CustomEvent)) return;

  const { outcome, type } = event.detail;

  if (outcome === 'applied') {
    showToast({ tone: 'success', title: copy.successTitle, body: copy.discountApplied });
    return;
  }

  showToast({
    tone: 'error',
    title: copy.errorTitle,
    body: type === 'shipping' ? copy.shippingDiscountError : copy.discountCodeError,
  });
});
